const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787/api';

// Base da API (com /api) — usada em links que o navegador abre direto (ex.: baixar documento do imóvel)
export const API_URL = BASE_URL;

// Origem do backend (sem /api) — os feeds XML públicos ficam em /xml/...
export const API_ORIGEM = new URL(BASE_URL).origin;

// Sessão via cookie httpOnly (não Bearer token) — `credentials: 'include'`
// é obrigatório em toda chamada, senão o cookie de sessão do painel-node
// nunca é enviado (origem diferente: Pages x Workers). Ver CORS/sameSite
// em painel-node (src/index.js, src/session.js).
//
// Erro 5xx em LEITURA (GET) é repetido até 2 vezes, com espera curta, sem o usuário ver: o
// servidor às vezes fica sem vaga no banco por alguns instantes (pool do Supabase cheio) e a
// mesma chamada funciona logo depois. Escrita (POST/PUT/DELETE) NUNCA é repetida sozinha —
// poderia gravar duas vezes.
const ESPERAS_REPETICAO_MS = [700, 1500];

async function fetchComRepeticao(url, init) {
  const ehLeitura = !init.method || init.method === 'GET';
  for (let tentativa = 0; ; tentativa++) {
    try {
      const resposta = await fetch(url, init);
      if (!(ehLeitura && resposta.status >= 500 && tentativa < ESPERAS_REPETICAO_MS.length)) return resposta;
    } catch (err) {
      if (!ehLeitura || tentativa >= ESPERAS_REPETICAO_MS.length) throw err;
    }
    await new Promise((resolve) => setTimeout(resolve, ESPERAS_REPETICAO_MS[tentativa]));
  }
}

async function chamar(caminho, opcoes = {}) {
  const resposta = await fetchComRepeticao(`${BASE_URL}${caminho}`, {
    ...opcoes,
    credentials: 'include',
    // FormData (upload de foto): o navegador monta o Content-Type com o boundary sozinho
    headers: {
      ...(opcoes.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...opcoes.headers,
    },
  });

  const corpo = await resposta.json().catch(() => ({}));
  if (!resposta.ok) {
    // 401 durante o uso (não só ao carregar a página) = sessão expirou no
    // servidor (KV do cookie venceu) — achado em auditoria 2026-09-18:
    // sem isso, cada tela tratava como "erro genérico" e o usuário nunca
    // era mandado de volta pro login sozinho. `AuthContext` escuta este
    // evento e desloga localmente; não chama `sair()` direto daqui
    // porque este módulo não é um componente React (sem acesso ao
    // contexto).
    if (resposta.status === 401) {
      window.dispatchEvent(new Event('painel:sessao-expirada'));
    }
    // "Erro interno do servidor" cru assusta e não ajuda: o usuário só precisa tentar de novo
    const mensagem = resposta.status >= 500 ? 'O servidor está ocupado agora. Tente de novo em instantes.' : corpo.error || `Erro ${resposta.status}`;
    const erro = new Error(mensagem);
    erro.status = resposta.status;
    erro.corpo = corpo;
    throw erro;
  }
  return corpo;
}

export const api = {
  get: (caminho) => chamar(caminho),
  post: (caminho, dados) => chamar(caminho, { method: 'POST', body: JSON.stringify(dados ?? {}) }),
  put: (caminho, dados) => chamar(caminho, { method: 'PUT', body: JSON.stringify(dados ?? {}) }),
  delete: (caminho) => chamar(caminho, { method: 'DELETE' }),
  upload: (caminho, formData) => chamar(caminho, { method: 'POST', body: formData }),
};
